import { useEffect, useState } from 'react';

import { useDebounce } from '../../utils/hooks/useDebounce';
import DateRangeControl from './DateRangeControl';

const selectClass =
  'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-white';

/**
 * The filter bar is composed from the fields the current page declares.
 *
 * That is the structural fix for a dashboard showing a control its endpoint
 * ignores: a page cannot render a filter it did not ask for, so the bar and the
 * request can never drift apart.
 */
export default function AiSpendFilterBar({
  fields,
  filters,
  setFilters,
  options,
  optionsLoading,
  activeFilterCount,
  onClear,
}) {
  const has = (field) => fields.includes(field);
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const debouncedSearch = useDebounce(searchDraft, 300);

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      setFilters({ search: debouncedSearch }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const multi = (field, label, list) => (
    <label key={field} className="flex flex-col gap-1 text-xs font-medium text-slate-500 dark:text-slate-400">
      {label}
      <select
        className={selectClass}
        value={filters[field][0] || ''}
        disabled={optionsLoading}
        onChange={(event) =>
          setFilters({ [field]: event.target.value ? [event.target.value] : [] })
        }
      >
        <option value="">All</option>
        {(list || []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-3">
      {has('range') && (
        <DateRangeControl
          value={{ date_from: filters.date_from, date_to: filters.date_to }}
          onChange={(range) => setFilters(range)}
          timezone={options?.timezone}
        />
      )}
      <div className="flex flex-wrap items-end gap-3">
        {has('provider') && multi('provider', 'Provider', options?.providers)}
        {has('feature') && multi('feature', 'Feature', options?.features)}
        {has('model') && multi('model', 'Model', options?.models)}
        {has('organization') && (
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-500 dark:text-slate-400">
            Organization
            <select
              className={selectClass}
              value={filters.organization}
              disabled={optionsLoading}
              onChange={(event) => setFilters({ organization: event.target.value })}
            >
              <option value="">All</option>
              {(options?.organizations || []).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {has('status') && (
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-500 dark:text-slate-400">
            Outcome
            <select
              className={selectClass}
              value={filters.status}
              onChange={(event) => setFilters({ status: event.target.value })}
            >
              <option value="all">All calls</option>
              <option value="succeeded">Succeeded</option>
              <option value="failed">Failed</option>
            </select>
          </label>
        )}
        {has('search') && (
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-500 dark:text-slate-400">
            Search
            <input
              type="search"
              className={selectClass}
              placeholder="model, email, error…"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
            />
          </label>
        )}
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={() => {
              setSearchDraft('');
              onClear();
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
          </button>
        )}
      </div>
    </div>
  );
}
